import { useState } from "react";
import { z } from "zod";
import { Button } from "@astryxdesign/core/Button";
import { TextInput } from "@astryxdesign/core/TextInput";
import { TextArea } from "@astryxdesign/core/TextArea";
import { VStack } from "@astryxdesign/core/VStack";
import { useT, type Messages } from "@/lib/i18n";

export interface Contacts {
  name: string;
  phone: string;
  email: string;
  comment: string;
}

function contactsSchema(m: Messages["contacts"]) {
  return z.object({
    name: z.string().trim().min(1, m.errName).max(80),
    phone: z
      .string()
      .transform((v) => v.replace(/[^\d+]/g, ""))
      .refine((v) => /^\+?\d{7,15}$/.test(v), m.errPhone),
    email: z.union([z.literal(""), z.email(m.errEmail)]),
    comment: z.string().max(500, m.errComment),
  });
}

/** Keyboard-friendly contact form: correct input modes, autocomplete, Enter advances. */
export function ContactsStep({ initial, onSubmit }: { initial: Contacts; onSubmit: (c: Contacts) => void }) {
  const t = useT();
  const [v, setV] = useState(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof Contacts, string>>>({});
  const set = (k: keyof Contacts) => (val: string) => setV((p) => ({ ...p, [k]: val }));

  const submit = () => {
    const r = contactsSchema(t.contacts).safeParse(v);
    if (!r.success) {
      const e: Partial<Record<keyof Contacts, string>> = {};
      for (const i of r.error.issues) e[i.path[0] as keyof Contacts] ??= i.message;
      setErrors(e);
      document.getElementById(`contact-${Object.keys(e)[0]}`)?.focus();
      return;
    }
    setErrors({});
    onSubmit({ ...v, name: v.name.trim() });
  };

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <VStack gap={3}>
        <TextInput
          id="contact-name"
          label={t.contacts.name}
          value={v.name}
          onChange={set("name")}
          autoComplete="name"
          isRequired
          status={errors.name ? { type: "error", message: errors.name } : undefined}
        />
        <TextInput
          id="contact-phone"
          label={t.contacts.phone}
          value={v.phone}
          onChange={set("phone")}
          autoComplete="tel"
          isRequired
          placeholder="+7 900 000-00-00"
          description={t.contacts.phoneHint}
          // TextInput forwards base props to the native input but its types omit inputMode.
          ref={(el: HTMLInputElement | null) => el?.setAttribute("inputmode", "tel")}
          status={errors.phone ? { type: "error", message: errors.phone } : undefined}
        />
        <TextInput
          id="contact-email"
          label={t.contacts.email}
          type="email"
          value={v.email}
          onChange={set("email")}
          autoComplete="email"
          isOptional
          status={errors.email ? { type: "error", message: errors.email } : undefined}
        />
        <TextArea
          label={t.contacts.comment}
          value={v.comment}
          onChange={set("comment")}
          isOptional
          rows={2}
          maxLength={500}
          status={errors.comment ? { type: "error", message: errors.comment } : undefined}
        />
        <Button type="submit" label={t.common.next} variant="primary" size="lg" width="100%" />
      </VStack>
    </form>
  );
}
